import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useAtomValue } from "jotai";
import { Button, Icon, Spinner } from "@common/index";
import { ComponentColor } from "@common/types";
import { ROUTES } from "@routing/routes";
import { useNotification } from "@components/Notification/hooks/useNotification";
import { useOrganizationApi } from "@/hooks/apiHooks/useOrganizationApi";
import { usePaymentMethodApi } from "@/hooks/apiHooks/usePaymentMethodApi";
import { useUserApi } from "@/hooks/apiHooks/useUserApi";
import { useAcceptUi, type AcceptUiCapture } from "@/hooks/useAcceptUi";
import { isProcessingPaymentMethodAtom } from "@/store/paymentMethodAtoms";
import type { OrganizationPaymentMethod } from "@models/organization";
import type {
    CreatePaymentMethodOutcome,
    CreatePaymentMethodResponse,
} from "@models/paymentMethod";
import { isMembershipWalled, type UserResponse } from "@models/user";
import {
    paymentMethodError,
    type PaymentMethodError,
} from "./_helpers/paymentMethodError";
import {
    savedCardMessage,
    unconfirmedSavedCardMessage,
} from "./_helpers/savedCardMessage";

// The card surface for both membership card paths (#375 / #376, DecisionLog §1.6).
// The card is collected inside Authorize.Net's hosted lightbox — this component only
// ever holds the opaque nonce, never a PAN or CVV.
//
// One endpoint, two truths:
//   "activate" — the AwaitingPaymentMethod wall. That org has never been charged, so
//                POSTing the nonce captures the card AND activates the membership.
//   "update"   — the membership tab (#376), which is where a lapsed or suspended
//                customer is sent. The endpoint UPSERTS: it stores the new profile
//                (deactivating the prior one), activates any approved location still
//                waiting for a card, and charges an outstanding renewal (#381).
//
// Whichever of those happened, the 200 reports it in `outcome`, and that alone
// decides what the success copy claims about the membership (MB-07). A save that
// did nothing beyond storing the card says so and nothing more.

type OrganizationLoadState = "loading" | "ready" | "missing" | "error";

// What the wall's post-save user refresh found. The wall says only what this
// has confirmed (review of PR 353): "lifted" is the one state that may say
// ordering is open, and the only one that toasts.
type WallRefreshState = "checking" | "lifted" | "walled" | "failed";

// Long enough to read on the page the customer lands on, short enough not to
// sit over it indefinitely.
const WALL_TOAST_MS = 8000;

export interface AddPaymentMethodPanelProps {
    /** Defaults to the #375 wall; "update" is the membership tab (#376). */
    variant?: "activate" | "update";
    /**
     * The organization's stored card, for the "update" variant's current-card line.
     * `undefined` is the pre-Team-B payload and is read exactly like `null`.
     */
    currentCard?: OrganizationPaymentMethod | null;
    /**
     * The organization to POST to, when the caller already has it. Omitted on the
     * wall, which has no organization in hand and must resolve one itself.
     */
    organizationId?: string;
    /**
     * The stored card changed, so the caller's copy of the organization is stale.
     * Fired once per attempt that left a new card on file: a successful capture
     * (after the user refresh is attempted) AND a failed save that stored the card
     * anyway (the membership charge after it did not go through).
     */
    onSaved?: () => void;
}

export const AddPaymentMethodPanel = ({
    variant = "activate",
    currentCard,
    organizationId: providedOrganizationId,
    onSaved,
}: AddPaymentMethodPanelProps) => {
    const { getMeOrganizations } = useOrganizationApi();
    const { createPaymentMethod } = usePaymentMethodApi();
    const { getMeUser } = useUserApi();
    const { showSuccess } = useNotification();
    const isProcessing = useAtomValue(isProcessingPaymentMethodAtom);
    const isUpdate = variant === "update";

    // Always null on the wall — that customer has by definition nothing on file, so
    // the prop is ignored there rather than trusted, and the default action label
    // stays "Add payment method" whatever a caller passes.
    const currentCardText = isUpdate ? describeCard(currentCard) : null;

    // Only the self-resolving path writes these; when the caller supplies an id
    // the effect below never runs and they stay at their initial values.
    const [fetchedOrganizationId, setFetchedOrganizationId] = useState<
        string | null
    >(null);
    const [fetchState, setFetchState] =
        useState<OrganizationLoadState>("loading");
    const [error, setError] = useState<PaymentMethodError | null>(null);
    const [saved, setSaved] = useState<CreatePaymentMethodResponse | null>(null);
    // Wall only; the membership tab never reads it.
    const [wallRefresh, setWallRefresh] =
        useState<WallRefreshState>("checking");

    // Derived, never synced into state through an effect: a caller-supplied id is
    // already resolved, so there is no loading window to render and none of the
    // "missing"/"error" states — the caller has dealt with those before it could
    // have had an id to pass. Both expressions test the same truthiness so the id
    // and the state can never disagree.
    const organizationId = providedOrganizationId || fetchedOrganizationId;
    const organizationState: OrganizationLoadState = providedOrganizationId
        ? "ready"
        : fetchState;

    // A refreshed user either lifts the wall or leaves it up. Only a lift
    // toasts (MB-07): the wall's redirect unmounts the inline confirmation, and
    // the app-level toast outlives it to greet the customer where they land,
    // then dismisses itself. A wall still up keeps everything inline.
    const settleWall = (
        user: UserResponse,
        outcome: CreatePaymentMethodOutcome | undefined,
    ) => {
        if (isMembershipWalled(user.membershipStatus)) {
            setWallRefresh("walled");
            return;
        }

        setWallRefresh("lifted");
        showSuccess({
            message: savedCardMessage(outcome),
            interval: WALL_TOAST_MS,
        });
    };

    const submitCapture = async (capture: AcceptUiCapture) => {
        // A capture can only happen once the org has resolved, but the id is what
        // the request is addressed to — never send the POST without it.
        if (!organizationId) return;

        setError(null);
        // The update path can capture again from its own success state, so a
        // stale confirmation must not outlive the attempt that replaces it: the
        // `saved` branch is checked before the action branch, so leaving it set
        // would render "Payment method updated" over a card the vendor has just
        // refused, with the alert nowhere on screen. Inert on the wall, whose
        // success state offers no way to start a second capture.
        setSaved(null);

        let response: CreatePaymentMethodResponse;
        try {
            response = await createPaymentMethod(organizationId, {
                paymentToken: capture.token,
            });
        } catch (failure) {
            const saveError = paymentMethodError(failure);
            setError(saveError);
            // Some 402s come AFTER the new card was stored, so the caller's
            // current-card line is stale — refresh it, as on success. The alert
            // stays: this panel's error state is untouched, and the membership
            // tab refreshes in place rather than remounting the panel.
            if (saveError.cardStored) onSaved?.();
            return;
        }

        setSaved(response);
        setWallRefresh("checking");

        // Refreshes currentUserAtom so the wall's own Active redirect fires — no
        // imperative navigate, no manual reload. Still done on the membership tab:
        // an AwaitingPaymentMethod customer updating from there genuinely does
        // activate, and other surfaces read the atom. The card is stored either way,
        // so a failure here must not read as a payment failure.
        try {
            const user = await getMeUser();
            if (!isUpdate) settleWall(user, response.outcome);
        } catch {
            // Wall only. The state drives the way-out control below, which exists
            // because the wall never lifts until currentUserAtom changes. Nobody is
            // stranded on the membership tab, so there the failure is swallowed.
            if (!isUpdate) setWallRefresh("failed");
        }

        // After the refresh attempt, and regardless of how it went: the card IS
        // stored, so the caller's copy of the organization is stale either way.
        onSaved?.();
    };

    // The only escape from a stranded success. Deliberately re-calls getMeUser
    // rather than linking to /c/browse: ApprovalGate reads the same stale
    // currentUserAtom and would bounce them straight back here.
    const retryRefresh = async () => {
        try {
            const user = await getMeUser();
            settleWall(user, saved?.outcome);
        } catch {
            /* leave the control in place so they can ask again */
        }
    };

    const { isConfigured, openLightbox } = useAcceptUi({
        onCapture: (capture) => {
            void submitCapture(capture);
        },
        // Vendor/lightbox failure — nothing reached our API and no card was ever
        // submitted, so this must not be labelled as a card problem.
        onError: (message) =>
            setError({ message, retry: "tryAgain", cardStored: false }),
    });

    // A user belongs to exactly one organization; the endpoint returns an array.
    // `silent`: a failure renders the "error" state below with its own Retry,
    // so the hook's toast would announce the same failure a second time. The
    // read's outcome is held in fetchState here; no atom reports it.
    const loadOrganization = useCallback(() => {
        getMeOrganizations({ silent: true })
            .then((organizations) => {
                const id = organizations[0]?.id;

                if (!id) {
                    setFetchState("missing");
                    return;
                }

                setFetchedOrganizationId(id);
                setFetchState("ready");
            })
            .catch(() => setFetchState("error"));
    }, [getMeOrganizations]);

    useEffect(() => {
        // Skipped entirely when payment is unconfigured: nothing on this panel can
        // work without the vendor keys, so there is no id worth fetching.
        if (!isConfigured) return;

        // The caller already has the organization. Reading it again would be a
        // second GET of an endpoint they just read. Data flows down instead.
        if (providedOrganizationId) return;

        loadOrganization();
    }, [isConfigured, providedOrganizationId, loadOrganization]);

    // Back to loading from the handler (not the effect), then re-read.
    const retryOrganization = () => {
        setFetchState("loading");
        loadOrganization();
    };

    let body: ReactNode;
    if (!isConfigured) {
        // Explicit dead end rather than a button that silently no-ops: without the
        // public vendor keys the lightbox cannot open at all.
        body = (
            <p className="text-sm text-neutral/70">
                Online payment isn&apos;t configured in this environment, so a
                payment method can&apos;t be added here yet.
            </p>
        );
    } else if (organizationState === "loading") {
        body = (
            <div className="flex justify-center py-6">
                <Spinner size="lg" />
            </div>
        );
    } else if (organizationState === "error") {
        // One read failed and nothing was attempted with a card, so the read
        // itself is the thing to repeat.
        body = (
            <div role="alert" className="flex flex-col items-start gap-3">
                <p className="text-sm text-neutral/80">
                    {ORGANIZATION_LOAD_FAILURE}
                </p>
                <div className="flex flex-wrap gap-3">
                    <Button
                        color={ComponentColor.Primary}
                        className="normal-case"
                        onClick={retryOrganization}
                    >
                        Retry
                    </Button>
                    <SupportButton />
                </div>
            </div>
        );
    } else if (organizationState === "missing") {
        body = (
            <div className="flex flex-col items-start gap-3">
                <p className="text-sm text-neutral/80">
                    We couldn&apos;t find an organization on your account, so
                    there&apos;s nothing to add a payment method to. Please
                    contact support.
                </p>
                <SupportButton />
            </div>
        );
    } else if (saved) {
        const card = describeCard(saved);

        const confirmation = (
            <div role="status" className="flex flex-col gap-2">
                <p className="flex items-center gap-2 text-sm font-semibold text-neutral">
                    <Icon name="check_circle" className="text-xl" />
                    <span>
                        {isUpdate
                            ? "Payment method updated"
                            : "Payment method saved"}
                    </span>
                </p>
                {card && <p className="text-sm text-neutral/80">{card}</p>}
                <p className="text-sm text-neutral/80">
                    {isUpdate
                        ? savedCardMessage(saved.outcome)
                        : wallMessage(wallRefresh, saved.outcome)}
                </p>
                {/* Only when the post-save refresh failed. Without it the wall
                    never moves and the customer is stranded having just paid. */}
                {wallRefresh === "failed" && (
                    <Button
                        color={ComponentColor.Primary}
                        className="normal-case"
                        onClick={() => void retryRefresh()}
                    >
                        Continue to shopping
                    </Button>
                )}
            </div>
        );

        // The wall's success is terminal by design: that customer is redirected
        // away the moment currentUserAtom refreshes. The membership tab is
        // outside ApprovalGate and nothing navigates away from it, so a terminal
        // success there strands anyone who entered the wrong card until they
        // reload by hand — a new dead end on the surface built to remove one.
        body = isUpdate ? (
            <div className="flex flex-col gap-4">
                {confirmation}
                <div className="flex flex-col items-start gap-3">
                    <CaptureAction
                        error={error}
                        isProcessing={isProcessing}
                        hasCard
                        onOpen={openLightbox}
                    />
                </div>
            </div>
        ) : (
            confirmation
        );
    } else {
        body = (
            <div className="flex flex-col items-start gap-3">
                {/* The update path opens with what is on file today: the action label
                    alone is too thin a signal for whether they are replacing a card
                    or adding their first one. */}
                {isUpdate && (
                    <p className="text-sm text-neutral/80">
                        {currentCardText ?? NO_CARD_ON_FILE}
                    </p>
                )}

                <CaptureAction
                    error={error}
                    isProcessing={isProcessing}
                    hasCard={currentCardText !== null}
                    onOpen={openLightbox}
                />
            </div>
        );
    }

    return (
        <section className="rounded-lg border border-base-300 bg-base-200 p-4 md:p-5 flex flex-col gap-4">
            <h2 className="font-semibold text-neutral">Payment Method</h2>

            {body}

            <p className="flex items-center gap-1 text-xs text-neutral/60">
                <Icon name="lock" className="text-sm" />
                All transactions are encrypted and processed securely.
            </p>
        </section>
    );
};

const SupportButton = () => (
    <Button
        to={ROUTES.SUPPORT}
        color={ComponentColor.Primary}
        outline
        className="normal-case"
    >
        Contact Support
    </Button>
);

interface CaptureActionProps {
    error: PaymentMethodError | null;
    isProcessing: boolean;
    /** Whether a card is already stored, which is what "update" vs "add" turns on. */
    hasCard: boolean;
    onOpen: () => void;
}

// The failure message plus whatever control that failure permits. Extracted
// because the update path renders it twice: once in the action state, and again
// beneath a success, where the customer may still need to correct the card they
// have just saved.
const CaptureAction = ({
    error,
    isProcessing,
    hasCard,
    onOpen,
}: CaptureActionProps) => (
    <>
        {error && (
            <p role="alert" className="text-sm text-error">
                {error.message}
            </p>
        )}

        {/* No retry control AT ALL when the failure is not one another card can
            fix: a button there would invite a charge the API has deliberately
            stopped, or a loop no card can break. */}
        {(!error || error.retry !== "none") && (
            <Button
                color={ComponentColor.Primary}
                className="normal-case"
                loading={isProcessing}
                // Deliberately does not clear `error`: until a new card is
                // captured, why the last attempt failed is still the truth.
                // Wrapped rather than passed directly, so the vendor call keeps
                // receiving no arguments where it used to receive none.
                onClick={() => onOpen()}
            >
                {!isProcessing && <Icon name="credit_card" className="text-xl" />}
                {buttonLabel(isProcessing, error, hasCard)}
            </Button>
        )}

        {/* Every one of those copy strings tells the customer to contact support,
            and this is the one state with no other control on screen — on the
            membership tab the surrounding sections can be empty too. An
            instruction with nothing to act on is a dead end. */}
        {error?.retry === "none" && <SupportButton />}
    </>
);

const NO_CARD_ON_FILE = "No payment method on file yet.";

// The wall's line under "Payment method saved", by what the user refresh found
// (review of PR 353). Only a lifted wall may say ordering is open. A wall that
// stays up after a good refresh is a save that activated nothing: say so and
// give the next step. Customers have no activation retry to call, so the next
// step is us.
const wallMessage = (
    state: WallRefreshState,
    outcome: CreatePaymentMethodOutcome | undefined,
): ReactNode => {
    switch (state) {
        case "lifted":
            return savedCardMessage(outcome);
        case "failed":
            return unconfirmedSavedCardMessage(outcome);
        case "walled":
            return (
                <>
                    Your card is saved, but your membership isn&apos;t active
                    yet.{" "}
                    <Link to={ROUTES.SUPPORT} className="link link-primary">
                        Contact us
                    </Link>{" "}
                    and we&apos;ll finish setting it up.
                </>
            );
        default:
            return "Your card is saved. Checking your membership…";
    }
};

// The organization read failed; the Retry beside it is the "try again", so this
// no longer says "refresh the page". GENERIC_FAILURE stays as it is — it is the
// capture path's floor copy, which has no Retry of its own.
const ORGANIZATION_LOAD_FAILURE =
    "Couldn't load your organization. Try again, or contact support if it keeps happening.";

const buttonLabel = (
    isProcessing: boolean,
    error: PaymentMethodError | null,
    hasCurrentCard: boolean,
): string => {
    if (isProcessing) return "Saving your card…";
    if (!error) {
        return hasCurrentCard ? "Update payment method" : "Add payment method";
    }

    // "Try a different card" only when the vendor actually refused a card. After a
    // lightbox failure nothing was ever submitted, so telling them to change cards
    // would send them hunting for a problem that isn't theirs.
    return error.retry === "differentCard"
        ? "Try a different card"
        : "Try again";
};

// Both fields are best-effort — the vendor's CIM create call returns no masked PAN,
// and the stored-card contract is nullable for the same reason — so every combination
// has to render. Returns null when there is nothing to say: the success headline
// already states the card was saved, and the update path substitutes NO_CARD_ON_FILE.
// Deliberately structural rather than typed to CreatePaymentMethodResponse, so the
// saved card and the stored card share one formatter instead of two that drift.
const describeCard = (
    card:
        | { cardBrand: string | null; cardLast4: string | null }
        | null
        | undefined,
): string | null => {
    const brand = card?.cardBrand?.trim();
    const last4 = card?.cardLast4?.trim();

    if (brand && last4) return `${brand} ending in ${last4}`;
    if (last4) return `Card ending in ${last4}`;

    return brand || null;
};
